import { useEffect, useState } from "react";
import { ArrowLeft, ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, CheckCircle2, ChevronDown, Search, Trash2, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import { SearchSelect } from "@/components/SearchSelect";
import { PaymentForm, MODES } from "./Payments";

export default function CashAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [action, setAction] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");
  const load = () => {
    api("/cash-accounts").then(setAccounts).catch((e) => setError(e.message));
    api("/cash-bank/summary").then(setSummary).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rowMenu) return;
    const close = () => setRowMenu(null);
    const onKey = (ev) => { if (ev.key === "Escape") setRowMenu(null); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("click", close); document.removeEventListener("keydown", onKey); };
  }, [rowMenu]);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(t);
  }, [message]);
  const openAction = (type, account) => { setRowMenu(null); setAction({ type, account }); };
  const onDone = (text) => { setAction(null); setMessage({ text, type: "success" }); load(); };
  const pager = usePagination(accounts || [], 25, "");
  if (error) return <div className="success-message error" data-testid="cash-error">{error}<button onClick={() => { setError(""); load(); }}>Coba lagi</button></div>;
  if (selected) return <AccountDetail account={selected} onBack={() => { setSelected(null); load(); }} />;
  if (!accounts) return <div className="report-loading" data-testid="cash-accounts-loading">Memuat Kas &amp; Bank...</div>;
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">KAS &amp; BANK</p>
          <h1>Kas &amp; Bank</h1>
        </div>
      </div>
      {message && <div data-testid="cash-action-message" className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button data-testid="cash-dismiss-message" onClick={() => setMessage(null)}><X size={14} /></button></div>}
      {summary && (
        <div className="cb-cards" data-testid="cashbank-summary-cards">
          <div className="cb-card green" data-testid="cb-incoming-card">
            <div className="cb-card-head"><span>Pemasukan 30 hari mendatang</span><i className="cb-badge green">{summary.incoming_30d.count}</i></div>
            <small>Total</small><strong>{money(summary.incoming_30d.total)}</strong>
          </div>
          <div className="cb-card red" data-testid="cb-outgoing-card">
            <div className="cb-card-head"><span>Pengeluaran 30 hari mendatang</span><i className="cb-badge red">{summary.outgoing_30d.count}</i></div>
            <small>Total</small><strong>{money(summary.outgoing_30d.total)}</strong>
          </div>
          <div className="cb-card blue" data-testid="cb-balance-card">
            <div className="cb-card-head"><span>Saldo kas &amp; bank</span><i className="cb-badge blue">{summary.cash_balance.count}</i></div>
            <small>Total</small><strong>{money(summary.cash_balance.total)}</strong>
          </div>
          <div className="cb-card blue" data-testid="cb-credit-card">
            <div className="cb-card-head"><span>Saldo kartu kredit</span><i className="cb-badge blue">0</i></div>
            <small>Total</small><strong>{money(0)}</strong>
          </div>
        </div>
      )}
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="cash-account-table">
          <thead><tr><th>KODE AKUN</th><th>NAMA AKUN</th><th className="right">SALDO BANK</th><th className="right">SALDO DI JURNAL</th><th>AKSI</th></tr></thead>
          <tbody>
            {pager.pageItems.map((a) => (
              <tr key={a.code} data-testid={`cash-account-${a.code}`} onClick={() => setSelected(a)}>
                <td className="primary-cell">{a.code}</td>
                <td><span className="cb-account-link">{a.name}</span></td>
                <td className="right mono">{money(a.reconciled_balance)}{a.last_date && <small className="cb-sub">{a.last_date}</small>}</td>
                <td className="right">{a.fully_reconciled ? <span className="status posted">Terekonsiliasi</span> : <span className={`mono ${a.balance < 0 ? "negative-amount" : ""}`}>{a.balance < 0 ? `(${money(Math.abs(a.balance))})` : money(a.balance)}</span>}</td>
                <td className="cb-menu-cell">
                  <button data-testid={`cash-create-tx-${a.code}`} className="table-action cb-menu-btn" onClick={(e) => { e.stopPropagation(); setRowMenu(rowMenu === a.code ? null : a.code); }}>Buat transaksi <ChevronDown size={12} /></button>
                  {rowMenu === a.code && (
                    <div className={`cb-menu ${pager.pageItems.indexOf(a) === pager.pageItems.length - 1 && pager.pageItems.length > 1 ? "up" : ""}`} data-testid={`cash-tx-menu-${a.code}`} onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`cash-receive-${a.code}`} onClick={() => openAction("RECEIVE", a)}><ArrowDownToLine size={13} /> Terima Uang</button>
                      <button data-testid={`cash-spend-${a.code}`} onClick={() => openAction("PAY", a)}><ArrowUpFromLine size={13} /> Pengeluaran Uang</button>
                      <button data-testid={`cash-transfer-${a.code}`} onClick={() => openAction("TRANSFER", a)}><ArrowLeftRight size={13} /> Transfer Uang</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination pager={pager} testid="cash-account-pagination" />
      </div>
      {action && action.type !== "TRANSFER" && (
        <div className="modal-overlay" data-testid="cash-payment-modal" onClick={() => setAction(null)}>
          <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
            <PaymentForm mode={action.type} cfg={MODES[action.type]} lockedAccount={action.account}
              onCancel={() => setAction(null)}
              onSaved={(record) => onDone(`${record.number} berhasil dicatat ke ${action.account.name} — jurnal kas otomatis diposting`)}
              onError={(text) => setMessage({ text, type: "error" })} />
          </div>
        </div>
      )}
      {action && action.type === "TRANSFER" && (
        <TransferModal account={action.account} accounts={accounts}
          onClose={() => setAction(null)}
          onSaved={(record) => onDone(`${record.number} — transfer ${money(record.amount)} dari ${record.from_name} ke ${record.to_name} berhasil dicatat`)} />
      )}
    </div>
  );
}

function TransferModal({ account, accounts, onClose, onSaved, initial }) {
  const [form, setForm] = useState(initial ? { from_account: initial.from_account, to_account: initial.to_account, amount: String(initial.amount), date: initial.date, memo: initial.memo || "" } : { from_account: account.code, to_account: "", amount: "", date: new Date().toISOString().slice(0, 10), memo: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });
  const targets = accounts.filter((a) => a.code !== form.from_account);
  const valid = form.to_account && Number(form.amount) > 0 && form.date;
  const submit = async () => {
    setSaving(true); setError("");
    try { onSaved(await api(initial ? `/transfers/${initial.id}` : "/transfers", { method: initial ? "PATCH" : "POST", body: JSON.stringify({ ...form, amount: Number(form.amount) }) })); }
    catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="cash-transfer-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head">
            <div><p className="eyebrow">KAS &amp; BANK</p><h2>{initial ? `Ubah Transfer ${initial.number}` : "Transfer Uang"}</h2></div>
            <button data-testid="transfer-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          {error && <div className="journal-balance bad" data-testid="transfer-error">{error}</div>}
          <div className="form-grid payment-head-grid">
            <label>Dari akun
              <select data-testid="transfer-from-select" value={form.from_account} disabled>
                <option value={account.code}>{`${account.code} · ${account.name} (${money(account.balance)})`}</option>
              </select>
            </label>
            <label>Ke akun
              <SearchSelect testid="transfer-to-select" value={form.to_account} onChange={(v) => set("to_account", v)}
                placeholder="Pilih akun tujuan..."
                options={targets.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}`, sub: money(a.balance) }))} />
            </label>
            <label>Jumlah<input data-testid="transfer-amount-input" type="number" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0" /></label>
            <label>Tanggal<input data-testid="transfer-date-input" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></label>
            <label>Memo<input data-testid="transfer-memo-input" value={form.memo} onChange={(e) => set("memo", e.target.value)} placeholder="Opsional" /></label>
          </div>
          <div className="form-actions">
            <button data-testid="transfer-save-button" className="primary-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : initial ? "Simpan Perubahan" : `Transfer ${money(Number(form.amount) || 0)}`}</button>
            <button data-testid="transfer-cancel-button" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountDetail({ account, onBack }) {
  const [tab, setTab] = useState("tx");
  const [tx, setTx] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [statement, setStatement] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const [checked, setChecked] = useState([]);
  const [error, setError] = useState("");
  const [editTransfer, setEditTransfer] = useState(null);
  const [cashList, setCashList] = useState([]);
  const [delTransfer, setDelTransfer] = useState(null);
  const [notice, setNotice] = useState("");
  const load = () => api(`/cash-accounts/${account.code}/transactions`).then(setTx).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [account.code]); // eslint-disable-line
  useEffect(() => { setChecked([]); }, [tab]);
  const toggle = async (entry) => {
    setMenuOpen(null);
    try {
      await api("/reconciliations/toggle", { method: "POST", body: JSON.stringify({ journal_id: entry.journal_id, account_code: account.code }) });
      load();
    } catch (e) { setError(e.message); }
  };
  const openEditTransfer = async (entry) => {
    setMenuOpen(null);
    try {
      const [transfers, accs] = await Promise.all([api("/transfers"), api("/cash-accounts")]);
      const t = transfers.find((x) => x.id === entry.transfer_id);
      if (!t) { setError("Data transfer tidak ditemukan"); return; }
      setCashList(accs);
      setEditTransfer(t);
    } catch (e) { setError(e.message); }
  };
  const removeTransfer = async () => {
    try {
      await api(`/transfers/${delTransfer.transfer_id}`, { method: "DELETE" });
      setError("");
      setNotice(`${delTransfer.reference || delTransfer.number} dihapus — jurnal transfer dibatalkan (void)`);
      load();
    } catch (e) { setError(e.message); }
    setDelTransfer(null);
  };
  const entries = (tx?.entries || []).filter((e) => {
    if (statusFilter === "matched" && !e.reconciled) return false;
    if (statusFilter === "unmatched" && e.reconciled) return false;
    return [e.number, e.date, e.reference, e.description, e.contact].join(" ").toLowerCase().includes(search.toLowerCase());
  });
  const unmatchedCount = tx ? tx.total_count - tx.reconciled_count : 0;
  const visibleEntries = tab === "recon" ? entries.filter((e) => !e.reconciled) : entries;
  const pager = usePagination(visibleEntries, 25, `${tab}|${statusFilter}|${search}`);
  const pageIds = [...new Set(pager.pageItems.map((e) => e.journal_id))];
  const allChecked = pageIds.length > 0 && pageIds.every((id) => checked.includes(id));
  const toggleCheck = (id) => setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setChecked(allChecked ? checked.filter((id) => !pageIds.includes(id)) : [...new Set([...checked, ...pageIds])]);
  const bulk = async (reconciled) => {
    try {
      await api("/reconciliations/bulk", { method: "POST", body: JSON.stringify({ account_code: account.code, journal_ids: checked, reconciled }) });
      setChecked([]);
      load();
    } catch (e) { setError(e.message); }
  };
  const diff = tx && statement !== "" ? Number(statement) - tx.reconciled_balance : null;
  return (
    <div className="workflow-page" data-testid="cash-account-detail">
      <button className="text-btn cb-back" data-testid="cash-detail-back" onClick={onBack}><ArrowLeft size={14} /> Kas &amp; bank</button>
      <div className="page-heading">
        <div><h1>({account.code}) - {account.name.toUpperCase()}</h1></div>
        <div className="report-highlight strong cash-total" data-testid="cash-detail-balance"><span>Saldo di Jurnal</span><strong>{money(tx?.closing_balance ?? account.balance)}</strong></div>
      </div>
      {error && <div className="success-message error" data-testid="cash-detail-error">{error}<button onClick={() => setError("")}>×</button></div>}
      {notice && <div className="success-message" data-testid="cash-detail-notice">{notice}<button onClick={() => setNotice("")}>×</button></div>}
      <div className="report-tabs" data-testid="cash-detail-tabs">
        <button data-testid="cash-tab-tx" className={tab === "tx" ? "active" : ""} onClick={() => setTab("tx")}>Transaksi Jurnal</button>
        <button data-testid="cash-tab-recon" className={tab === "recon" ? "active" : ""} onClick={() => setTab("recon")}>Rekonsiliasi <i className="cb-badge blue inline" data-testid="recon-count-badge">{unmatchedCount}</i></button>
      </div>
      {!tx && <div className="report-loading" data-testid="cash-tx-loading">Memuat transaksi...</div>}
      {tx && tab === "recon" && (
        <div className="recon-summary" data-testid="recon-summary">
          <div className="recon-chip"><small>Saldo tercatat</small><strong data-testid="recon-recorded-balance">{money(tx.closing_balance)}</strong></div>
          <div className="recon-chip ok"><small>Terekonsiliasi ({tx.reconciled_count}/{tx.total_count})</small><strong data-testid="recon-matched-balance">{money(tx.reconciled_balance)}</strong></div>
          <div className="recon-chip warn"><small>Belum terekonsiliasi</small><strong data-testid="recon-unmatched-balance">{money(tx.unreconciled_balance)}</strong></div>
          <label className="recon-statement">Saldo rekening koran
            <input data-testid="recon-statement-input" type="number" value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="Masukkan saldo bank" />
          </label>
          {diff !== null && (
            <div className={`journal-balance ${Math.abs(diff) < 1 ? "ok" : "bad"} recon-diff`} data-testid="recon-diff-indicator">
              {Math.abs(diff) < 1 ? "Cocok! Rekonsiliasi seimbang" : `Selisih ${money(diff)}`}
            </div>
          )}
        </div>
      )}
      {tx && (
        <>
          <div className="workflow-toolbar surface">
            <select data-testid="cash-status-filter" className="cb-filter" value={tab === "recon" ? "unmatched" : statusFilter} onChange={(e) => setStatusFilter(e.target.value)} disabled={tab === "recon"}>
              <option value="">Semua status</option>
              <option value="matched">Terekonsiliasi</option>
              <option value="unmatched">Belum terekonsiliasi</option>
            </select>
            <div className="workflow-search"><Search size={16} /><input data-testid="cash-tx-search" placeholder="Cari transaksi Jurnal..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
            <span className="record-count">{visibleEntries.length} transaksi</span>
          </div>
          {checked.length > 0 && (
            <div className="bulk-bar surface" data-testid="recon-bulk-bar">
              <strong data-testid="recon-bulk-count">{checked.length} transaksi dipilih</strong>
              <button data-testid="recon-bulk-match" className="primary-btn small" onClick={() => bulk(true)}><CheckCircle2 size={14} /> Cocokkan yang dipilih</button>
              {tab === "tx" && <button data-testid="recon-bulk-unmatch" className="secondary-btn small" onClick={() => bulk(false)}>Batalkan rekonsiliasi</button>}
              <button data-testid="recon-bulk-clear" className="text-btn" onClick={() => setChecked([])}>Bersihkan pilihan</button>
            </div>
          )}
          <div className="surface workflow-table-wrap">
            <table className="workflow-table" data-testid="cash-transaction-table">
              <thead><tr><th className="check-col"><input type="checkbox" data-testid="cash-tx-check-all" checked={allChecked} onChange={toggleAll} /></th><th>TANGGAL</th><th>NO. TRANSAKSI</th><th>NAMA KONTAK</th><th className="right">TERIMA (IDR)</th><th className="right">KIRIM (IDR)</th><th className="right">SALDO (IDR)</th><th>STATUS</th><th>TINDAKAN</th></tr></thead>
              <tbody>
                {pager.pageItems.map((e, i) => (
                  <tr key={e.journal_id + i} className={e.reconciled ? "recon-row-matched" : ""} data-testid={`cash-tx-${i}`}>
                    <td className="check-col"><input type="checkbox" data-testid={`cash-tx-check-${i}`} checked={checked.includes(e.journal_id)} onChange={() => toggleCheck(e.journal_id)} onClick={(ev) => ev.stopPropagation()} /></td>
                    <td>{e.date}</td>
                    <td><span className="cb-account-link">{e.number}</span>{e.reference && <small className="cb-sub">{e.reference}</small>}</td>
                    <td>{e.contact || "—"}</td>
                    <td className="right mono">{e.debit ? money(e.debit) : "0,00"}</td>
                    <td className="right mono">{e.credit ? money(e.credit) : "0,00"}</td>
                    <td className="right mono">{money(e.balance)}</td>
                    <td>{e.reconciled ? <span className="status posted" data-testid={`cash-tx-status-${i}`}>Terekonsiliasi</span> : <span className="status draft" data-testid={`cash-tx-status-${i}`}>Belum</span>}</td>
                    <td className="cb-menu-cell">
                      <button data-testid={`cash-tx-action-${i}`} className="table-action cb-menu-btn" onClick={() => setMenuOpen(menuOpen === i ? null : i)}>Tindakan <ChevronDown size={12} /></button>
                      {menuOpen === i && (
                        <div className="cb-menu" data-testid={`cash-tx-menu-${i}`}>
                          <button data-testid={`cash-tx-toggle-${i}`} onClick={() => toggle(e)}><CheckCircle2 size={13} /> {e.reconciled ? "Batalkan rekonsiliasi" : "Cocokkan"}</button>
                          {e.transfer_id && <button data-testid={`cash-tx-edit-transfer-${i}`} onClick={() => openEditTransfer(e)}><ArrowLeftRight size={13} /> Ubah transfer</button>}
                          {e.transfer_id && <button data-testid={`cash-tx-delete-transfer-${i}`} onClick={() => { setMenuOpen(null); setDelTransfer(e); }}><Trash2 size={13} /> Hapus transfer</button>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visibleEntries.length === 0 && <div className="empty-state" data-testid="cash-tx-empty"><strong>Tidak ada transaksi</strong><span>{tab === "recon" ? "Semua transaksi sudah terekonsiliasi." : "Tidak ada mutasi yang cocok dengan filter."}</span></div>}
            <Pagination pager={pager} testid="cash-tx-pagination" />
          </div>
        </>
      )}
      {editTransfer && (
        <TransferModal
          account={cashList.find((a) => a.code === editTransfer.from_account) || { code: editTransfer.from_account, name: editTransfer.from_name, balance: 0 }}
          accounts={cashList} initial={editTransfer}
          onClose={() => setEditTransfer(null)}
          onSaved={(r) => { setEditTransfer(null); setError(""); setNotice(`${r.number} berhasil diubah — jurnal transfer dibuat ulang`); load(); }} />
      )}
      {delTransfer && (
        <div className="modal-overlay" data-testid="transfer-delete-modal" onClick={() => setDelTransfer(null)}>
          <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
            <div className="section-head">
              <div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Transfer?</h2></div>
              <button className="icon-btn" onClick={() => setDelTransfer(null)}><X size={18} /></button>
            </div>
            <p className="muted delete-warning">"{delTransfer.number}" akan dihapus dan jurnal transfernya dibatalkan (void). Saldo kedua akun akan kembali seperti sebelum transfer.</p>
            <div className="form-actions">
              <button data-testid="transfer-delete-confirm" className="danger-btn" onClick={removeTransfer}>Ya, Hapus</button>
              <button data-testid="transfer-delete-cancel" className="secondary-btn" onClick={() => setDelTransfer(null)}>Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
